import { FC, memo, MouseEvent, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { AnimatePresence } from "framer-motion";

import { TestingAudienceResource } from "@/types/resources";
import { ActionsWrap, Td } from "@/components/Table";
import Text from "@/components/Text";
import ActionsDropdown, {
  ActionsDropdownButton,
} from "@/components/ActionsDropdown";
import { ReactComponent as PencilIcon } from "@/assets/icons/pencil.svg";
import { ReactComponent as TrashIcon } from "@/assets/icons/trashbin.svg";
import TestingAudienceForm from "./TestingAudienceForm";
import useAlerts from "@/hooks/useAlerts";
import { api } from "@/utils/api";

interface Props {
  audience: TestingAudienceResource;
}

const TestingAudienceRow: FC<Props> = memo(({ audience }) => {
  const [showEditForm, setShowEditForm] = useState(false);

  const alerts = useAlerts();

  const queryClient = useQueryClient();

  const onDelete = async (e: MouseEvent<HTMLButtonElement>) => {
    try {
      e.stopPropagation();

      const confirmed = await alerts.confirm(
        "Are you sure you want to delete this audience?",
        audience.name
      );

      if (!confirmed) return;

      await api.delete(`/v1/testing-audiences/${audience.id}`);

      queryClient.invalidateQueries(["testing_audiences"]);

      alerts.success("Testing audience successfully deleted", audience.name);
    } catch (_) {
      alerts.error();
    }
  };
  return (
    <tr>
      <Td>
        <Text $type="smallHeadline" as="span">
          {audience.name}
        </Text>
      </Td>
      <Td>{audience.channels_count}</Td>
      <Td>{audience.notes || "-"}</Td>
      <Td>
        <ActionsWrap>
          <ActionsDropdown>
            <ActionsDropdownButton
              onClick={() => setShowEditForm(true)}
              as="button"
            >
              <span>
                <PencilIcon />
              </span>
              Edit
            </ActionsDropdownButton>
            <ActionsDropdownButton onClick={onDelete} as="button">
              <span>
                <TrashIcon />
              </span>
              Delete
            </ActionsDropdownButton>
          </ActionsDropdown>
          <AnimatePresence>
            {showEditForm && (
              <TestingAudienceForm
                onClose={() => setShowEditForm(false)}
                editableAudience={audience}
              />
            )}
          </AnimatePresence>
        </ActionsWrap>
      </Td>
    </tr>
  );
});

export default TestingAudienceRow;
